import React from "react";
import { NotificationItem } from "./NotificationItem";
import {
  Notification,
  NotificationStatus,
  NotificationTypeJS,
} from "target_wasm";

interface FilterCategoriesState {
  all: boolean;
  requests: boolean;
  updates: boolean;
}

interface NotificationListProps {
  notifications: Notification[];
  activeFilter: "all" | "unread";
  filterCategories: FilterCategoriesState;
  justMarkedAsSeenId: string | null;
  onItemClick: (notification: Notification) => void;
  onNotificationUpdate?: () => void;
  onNavigateToTeam?: () => void;
}

const getNotificationCategory = (
  notification: Notification,
): "requests" | "updates" => {
  const type = notification.notification_type_js();
  if (
    type === NotificationTypeJS.TableRequest ||
    type === NotificationTypeJS.TableOwnershipTransferRequest ||
    type === NotificationTypeJS.OrgInvite
  ) {
    return "requests";
  }
  return "updates";
};

const isFilteredByRequests = (
  filterCategories: FilterCategoriesState,
): boolean =>
  !filterCategories.all &&
  filterCategories.requests &&
  !filterCategories.updates;

const isOwnershipTransferHandled = (notification: Notification): boolean => {
  if (
    notification.notification_type_js() !==
    NotificationTypeJS.TableOwnershipTransferRequest
  ) {
    return false;
  }
  try {
    const data = notification.get_data_js();
    const status = (data?.status || "").toString().toLowerCase();
    return (
      status === "accepted" ||
      status === "rejected" ||
      status === "declined" ||
      status === "cancelled"
    );
  } catch {
    return false;
  }
};

const isRequestHandled = (notification: Notification): boolean => {
  const type = notification.notification_type_js();
  if (type === NotificationTypeJS.TableRequest)
    return isTableRequestHandled(notification);
  if (type === NotificationTypeJS.TableOwnershipTransferRequest)
    return isOwnershipTransferHandled(notification);
  return false;
};

const isTableRequestHandled = (notification: Notification): boolean => {
  const type = notification.notification_type_js();
  if (type !== NotificationTypeJS.TableRequest) {
    return false;
  }

  try {
    const data = notification.get_data_js();
    const status = (data?.request_status || "").toString().toLowerCase();
    return (
      status === "accepted" || status === "rejected" || status === "declined"
    );
  } catch {
    return false;
  }
};

export const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  activeFilter,
  filterCategories,
  justMarkedAsSeenId,
  onItemClick,
  onNotificationUpdate,
  onNavigateToTeam,
}) => {
  const categoryFilteredNotifications = notifications.filter((notification) => {
    if (
      filterCategories.all ||
      (!filterCategories.requests && !filterCategories.updates)
    )
      return true;
    const category = getNotificationCategory(notification);
    return filterCategories[category];
  });

  const filteredNotifications =
    activeFilter === "unread"
      ? categoryFilteredNotifications.filter(
          (notification) =>
            notification.status_js() === NotificationStatus.Pending,
        )
      : categoryFilteredNotifications;

  const displayNotifications = isFilteredByRequests(filterCategories)
    ? (() => {
        const unhandled: Notification[] = [];
        const handled: Notification[] = [];
        for (const n of filteredNotifications) {
          if (isRequestHandled(n)) handled.push(n);
          else unhandled.push(n);
        }
        return [...unhandled, ...handled];
      })()
    : filteredNotifications;

  if (filteredNotifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 select-none">
        <p className="text-fg-medium text-center">
          {activeFilter === "unread"
            ? "No unread notifications"
            : "No notifications"}
        </p>
        <p className="text-fg-muted-regular text-center mt-1">
          You're all caught up
        </p>
      </div>
    );
  }

  return (
    <ul className=" max-h-96 overflow-y-auto overflow-x-hidden">
      {displayNotifications.map((notification) => (
        <NotificationItem
          key={notification.notification_id_js()}
          notification={notification}
          onClick={() => onItemClick(notification)}
          onNotificationUpdate={onNotificationUpdate}
          markedAsSeen={
            justMarkedAsSeenId === notification.notification_id_js()
          }
          onNavigateToTeam={onNavigateToTeam}
        />
      ))}
    </ul>
  );
};
